import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import {
  fetchArticles,
  fetchResume,
  fetchSelection,
  type Article,
  type Resume,
  type Selection,
} from "./articles";
import { useParticipation } from "./useParticipation";

/**
 * LA BILLETTERIE DE CET ÉVÉNEMENT, ET CE QUE CETTE PERSONNE Y A RETENU.
 *
 * ⚠️ TOUT EXIGE UNE SESSION, jusqu'au catalogue. `router.get("/", isPrivate, …)` :
 * lister les articles d'un événement demande déjà un jeton, à la différence des jours
 * ou des typologies qui voyagent avec le contexte public. Le fil du tunnel ne peut donc
 * pas s'appuyer sur « cet événement a-t-il des articles ? » pour décider si l'étape
 * existe — c'est la même contrainte que les conférences, et elle explique pourquoi les
 * deux étapes s'AJOUTENT au parcours au lieu d'en être retirées (voir `optIn` dans le
 * registre).
 */
export function useArticles(
  /**
   * Faut-il seulement demander ? Le récapitulatif de la confirmation ne le fait que si
   * l'étape est du parcours : sinon c'est deux requêtes sur chaque confirmation de
   * chaque événement, dont l'immense majorité ne vend rien.
   */
  active: boolean,
  /** La langue lue, pour choisir entre `label` et `labelEN`. */
  locale: string,
): {
  articles: ReadonlyArray<Article>;
  /** Ce que le serveur retient déjà — `undefined` tant qu'on ne le sait pas. */
  saved: Selection | undefined;
  loading: boolean;
  /**
   * Le catalogue OU la sélection existante n'a pas pu être lu — dit, plutôt que de rendre
   * une liste qui, cliquée sans connaître l'existant, l'écraserait à la confirmation.
   */
  failure: boolean;
} {
  const event = useEvent();
  const { me } = useParticipation();

  /* La langue fait partie de la clé : elle décide du libellé servi, donc deux langues
     ne peuvent pas partager une réponse en cache. */
  const english = locale.toLowerCase().startsWith("en");

  const catalogue = useQuery({
    queryKey: ["articles", event?.domainId, event?.eventId, english],
    queryFn: ({ signal }) =>
      fetchArticles(event!.domainId, event!.eventId, english, signal),
    enabled: active && !!event,
    /* Le catalogue est une donnée d'organisateur : il ne bouge pas pendant qu'on
       s'inscrit, contrairement aux places de conférence qui se prennent en direct. */
    staleTime: Infinity,
    retry: false,
  });

  const selection = useQuery({
    queryKey: ["articles-retenus", event?.domainId, event?.eventId, me?.id],
    queryFn: ({ signal }) =>
      fetchSelection(event!.domainId, event!.eventId, me!.id, signal),
    enabled: active && !!event && typeof me?.id === "number",
    staleTime: Infinity,
    retry: false,
  });

  return {
    articles: catalogue.data ?? [],
    /* ⚠️ `undefined` TANT QU'ON NE SAIT PAS, et surtout pas `{}`. L'écran s'en sert pour
       distinguer « il n'a rien retenu » de « on ne l'a pas encore lu » : confondus, les
       cases se décochent sous la main du participant au retour de la requête. */
    saved: selection.data,
    loading: active && (catalogue.isPending || selection.isPending),
    // ⚠️ ET L'ÉCHEC DE LA SÉLECTION AUSSI. Sans elle, on ne sait pas ce qui était retenu ;
    // rendre la liste laisserait cliquer, et la confirmation écraserait la commande.
    failure: catalogue.isError || selection.isError,
  };
}

/**
 * CE QUE CETTE SÉLECTION COÛTERAIT — calculé par le serveur, à chaque changement.
 *
 * ⚠️ UNE REQUÊTE PAR CLIC, ET C'EST VOULU. Le prix d'un article n'est pas son prix :
 * il dépend des gratuits acquis, du contingent restant d'un code de remise et de ce
 * qui a déjà été facturé — voir `funnel/articles.ts`. Le recalculer dans le navigateur
 * afficherait un total que la facture démentirait ensuite. La réponse précédente reste
 * affichée pendant la nouvelle (`placeholderData`), sans quoi le récapitulatif
 * disparaîtrait à chaque case cochée.
 */
/**
 * ═══ CE SOLDE DÉPEND DE CE QUE LA CLÉ NE DIT PAS ═══
 *
 * ⚠️ SANS SÉLECTION, LE SERVEUR FACTURE CE QUI EST ENREGISTRÉ. La réponse dépend donc de
 * la COMMANDE EN BASE, qui n'apparaît nulle part dans la clé — et une clé qui ne nomme
 * pas ce dont sa réponse dépend ne peut pas se périmer toute seule. C'est la cause des
 * deux défauts signalés le 2026-08-05 : un montant qui restait celui d'avant.
 *
 * TOUT CE QUI ÉCRIT LA COMMANDE DOIT DONC PÉRIMER `articles-resume` À LA MAIN. À ce
 * jour, cinq endroits, et ils sont commentés chacun chez lui :
 *
 *   `useParticipation`      l'enregistrement du parcours écrit la sélection
 *   `CompanionSteps`        la commande d'un accompagnant est facturée au porteur
 *   `Companions` (retrait)  retirer quelqu'un retire ses billets
 *   `PromoCode`             le code promo s'écrit tout de suite, la remise avec (#362)
 *   le paiement             il revient par un rechargement complet — rien à faire
 *
 * Un sixième écrivain apparaîtra un jour : c'est cette liste qu'il faut rallonger.
 */
export function useResume(
  /** `null` : ce que doit la commande ENREGISTRÉE. Une liste : cette sélection-ci. */
  ids: ReadonlyArray<number> | null,
  active: boolean,
): {
  resume: Resume | undefined;
  loading: boolean;
  /**
   * UNE RELECTURE EST EN VOL. `resume` est alors encore la réponse d'AVANT (gardée pendant
   * la relecture) : qui décide sur le solde, et pas seulement l'affiche, attend qu'elle
   * revienne. Voir l'email de la confirmation.
   */
  fetching: boolean;
  failure: boolean;
} {
  const event = useEvent();
  const { me } = useParticipation();

  const q = useQuery({
    queryKey: resumeKey(event?.domainId, event?.eventId, me?.id, ids),
    queryFn: ({ signal }) =>
      fetchResume(event!.domainId, event!.eventId, me!.id, ids, { signal }),
    enabled: active && !!event && typeof me?.id === "number",
    placeholderData: (previous) => previous,
    retry: false,
  });

  return {
    resume: q.data,
    loading: active && q.isPending,
    fetching: q.isFetching,
    failure: q.isError,
  };
}

/**
 * Les identifiants font partie de la clé, doublons compris : c'est la sélection exacte
 * qu'on fait chiffrer. Triés pour que deux sélections identiques dans un ordre différent
 * ne redemandent pas la même réponse.
 *
 * ⚠️ ET `null` NE S'ÉCRIT PAS COMME UNE LISTE VIDE. Les deux donnaient la chaîne vide : la
 * confirmation, qui demande le solde enregistré, partageait donc sa réponse avec l'étape
 * qui vient de vider son panier — deux questions différentes sous une seule clé.
 */
function resumeKey(
  domainId: number | undefined,
  eventId: number | undefined,
  userId: number | undefined,
  ids: ReadonlyArray<number> | null,
) {
  return [
    "articles-resume",
    domainId,
    eventId,
    userId,
    ids === null ? "enregistre" : [...ids].sort((a, b) => a - b).join(","),
  ];
}

/**
 * LE SOLDE DE LA COMMANDE ENREGISTRÉE, DEMANDÉ HORS D'UN ÉCRAN : par « Enregistrer », juste
 * après son écriture, pour savoir si l'email de confirmation peut partir.
 *
 * ⚠️ LA CLÉ DE `useResume(null, …)`, et pas une seconde : la confirmation qui s'ouvre
 * ensuite retrouve cette réponse en cache au lieu de la redemander, et les deux ne
 * peuvent pas se contredire. L'écriture vient de la périmer (`settleWrite`), donc elle
 * est relue ici, jamais resservie.
 */
export function fetchSavedResume(
  client: QueryClient,
  domainId: number,
  eventId: number,
  userId: number,
): Promise<Resume> {
  return client.fetchQuery({
    queryKey: resumeKey(domainId, eventId, userId, null),
    queryFn: ({ signal }) =>
      fetchResume(domainId, eventId, userId, null, { signal }),
    retry: false,
  });
}
